import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  Typography,
  Paper,
  Select,
  MenuItem,
  Divider,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  IconButton,
  Container,
  Card,
  CardContent,
  Chip,
  Tooltip,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import {
  Church as ChurchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  SportsEsports as SportsIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
} from "@mui/icons-material";
import { bqApi } from "../services/api/bqApi";
import { churchApi, Church } from "../services/api/churchApi";
import { eventApi, IEvent } from "../services/api/eventApi";
import { BQBasicInfo, BQParticipant, BQObserver } from "../types/bq";
import moment from "moment";

const BQRegisterPage: React.FC = () => {
  const { eventId, churchId } = useParams<{ eventId: string; churchId?: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [churchInfo, setChurchInfo] = useState<{ mainId: string; subId: string; name: string; managerName?: string; phone?: string } | null>(null);
  const [churches, setChurches] = useState<Church[]>([]);
  const [churchSearchValue, setChurchSearchValue] = useState<string>("");
  const [churchSearchLoading, setChurchSearchLoading] = useState(false);
  const [photoPaths, setPhotoPaths] = useState<{ [key: string]: string }>({});
  const [selectedChurchId, setSelectedChurchId] = useState<string | null>(null);
  const [eventInfo, setEventInfo] = useState<IEvent | null>(null);
  const [refundAmount, setRefundAmount] = useState<number>(0); // 환불 금액 추적
  const [originalCosts, setOriginalCosts] = useState<number>(0); // 기존 등록의 원래 참가비 (환불 적용 전)
  const [initialCosts, setInitialCosts] = useState<number>(0); // 현재 세션에서 최초 계산된 참가비 (인원 추가 시 업데이트)
  const [isExistingRegistration, setIsExistingRegistration] = useState<boolean>(false); // 기존 등록인지 여부
  const [savedTotalCosts, setSavedTotalCosts] = useState<number>(0); // 저장된 총 참가비 (환불 적용된 금액)
  const [savedParticipantCount, setSavedParticipantCount] = useState<string>(''); // 저장된 인원 수 (인원 변경 감지용)
  const [tempTotalCosts, setTempTotalCosts] = useState<number>(0); // 임시 총 참가비 (접수 완료 이전 변경 사항 반영)
  const [tempParticipantCount, setTempParticipantCount] = useState<string>(''); // 임시 인원 수 (접수 완료 이전 변경 사항 반영)
  
  const [basicInfo, setBasicInfo] = useState<BQBasicInfo>({
    id: 1000,
    region: "",
    church_number: "",
    church_name: "",
    representative: "",
    contact: "",
  });
  
  // 전화번호 포맷팅 함수
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const [sparksList, setSparksList] = useState<BQParticipant[]>([]);
  const [ttList, setTtList] = useState<BQParticipant[]>([]);
  const [observerInputs, setObserverInputs] = useState<{ [key: number]: string }>({});
  const [observers, setObservers] = useState<BQObserver[]>([
    {
      id: 1,
      observerName: "Sparks 클럽원 (선수 외의)",
      observerCount: 0,
      hasMeal: true,
      hasPin: true,
    },
    {
      id: 2,
      observerName: "Sparks 교사 (코치 외의)",
      observerCount: 0,
      hasMeal: true,
      hasPin: true,
    },
    {
      id: 3,
      observerName: "T&T 클럽원 (선수 외의)",
      observerCount: 0,
      hasMeal: true,
      hasPin: true,
    },
    {
      id: 4,
      observerName: "T&T 교사 (코치 외의)",
      observerCount: 0,
      hasMeal: true,
      hasPin: true,
    },
    {
      id: 5,
      observerName: "학부모 포함 식권 별도 구입인원",
      observerCount: 0,
      hasMeal: true,
      hasPin: false,
    },
  ]);

  // 이벤트 정보 조회
  useEffect(() => {
    const fetchEventInfo = async () => {
      if (!eventId) return;
      
      try {
        const events = await eventApi.getEvents();
        const event = events.find(e => e._id === eventId);
        if (event) {
          console.log('이벤트 정보 로드:', {
            eventId: event._id,
            refundDeadline: event.bq_Refund_Deadline,
            refundPercentage: event.bq_Refund_Percentage,
            isAfterDeadline: event.bq_Refund_Deadline ? moment().isAfter(moment(event.bq_Refund_Deadline)) : false
          });
          setEventInfo(event);
        }
      } catch (err: any) {
        console.error("Error fetching event info:", err);
      }
    };

    fetchEventInfo();
  }, [eventId]);

  // 교회 정보 조회
  useEffect(() => {
    const fetchChurchInfo = async () => {
      if (!churchId) return;
      
      try {
        setLoading(true);
        const [mainId, subId] = churchId.split('-');
        const response = await churchApi.getChurchById(mainId, subId);
        
        if (response.success && response.data) {
          setChurchInfo({
            mainId: response.data.mainId,
            subId: response.data.subId,
            name: response.data.name,
            managerName: response.data.managerName,
            phone: response.data.phone,
          });
          
          // 교회 정보로 기본 정보 채우기
          const formattedPhone = response.data.phone ? formatPhoneNumber(response.data.phone) : "";
          setBasicInfo(prev => ({
            ...prev,
            church_name: response.data.name,
            representative: response.data.managerName || "",
            contact: formattedPhone,
            church_number: mainId,
          }));
        }
      } catch (err: any) {
        setError(err.message || "교회 정보를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchChurchInfo();
  }, [churchId]);

  // 기존 등록 데이터 조회 (URL 파라미터 churchId 또는 선택된 교회)
  useEffect(() => {
    const fetchExistingData = async () => {
      const currentChurchId = churchId || selectedChurchId;
      if (!eventId || !currentChurchId) return;
      
      try {
        setLoading(true);
        const data = await bqApi.getRegistration(eventId, currentChurchId);
        
        // 교회 ID로 교회명 조회
        let fetchedChurchName = basicInfo.church_name;
        if (currentChurchId && !fetchedChurchName) {
          try {
            const [mainId, subId] = currentChurchId.split('-');
            const churchResponse = await churchApi.getChurchById(mainId, subId);
            if (churchResponse.success && churchResponse.data) {
              fetchedChurchName = churchResponse.data.name;
            }
          } catch (err) {
            console.error("Error fetching church name:", err);
          }
        }
        
        if (data.basicInfo) {
          // 전화번호 포맷팅 적용
          const formattedContact = data.basicInfo.contact ? formatPhoneNumber(data.basicInfo.contact) : "";
          setBasicInfo({
            ...data.basicInfo,
            church_name: fetchedChurchName || data.basicInfo.church_name || basicInfo.church_name,
            contact: formattedContact,
          });
          
          // 기존 등록의 원래 참가비 계산 (현재 인원 기준)
          // 기존 등록 데이터를 기반으로 원래 참가비를 계산하여 저장
          // 나중에 인원이 줄어들면 이 값을 기준으로 환불 금액 계산
          const existingSparksCount = data.sparksList?.length || 0;
          const existingTtCount = data.ttList?.length || 0;
          const existingObservers = data.observers || [];
          
          // 기존 코치 수 계산 (calculateCosts와 동일한 방식)
          const existingSparksCoaches = new Set(
            (data.sparksList || [])
              .filter((p: any) => p.coachName?.trim() && p.coachContact?.trim())
              .map((p: any) => `${p.coachName}-${p.coachContact}`)
          ).size;
          const existingTtCoaches = new Set(
            (data.ttList || [])
              .filter((p: any) => p.coachName?.trim() && p.coachContact?.trim())
              .map((p: any) => `${p.coachName}-${p.coachContact}`)
          ).size;
          
          // calculateCosts와 동일한 방식으로 선수 수 계산
          // playerName이 있는 항목만 선수로 카운트
          const existingSparksPlayers = (data.sparksList || []).filter(
            (item: any) => item.playerName?.trim()
          ).length;
          const existingTtPlayers = (data.ttList || []).filter(
            (item: any) => item.playerName?.trim()
          ).length;
          
          const existingObserverCosts = 
            (existingObservers[0]?.observerCount || 0) * 12000 +
            (existingObservers[1]?.observerCount || 0) * 12000 +
            (existingObservers[2]?.observerCount || 0) * 12000 +
            (existingObservers[3]?.observerCount || 0) * 12000 +
            (existingObservers[4]?.observerCount || 0) * 8000;
          
          const existingBaseCost = 
            existingSparksPlayers * 25000 +
            existingSparksCoaches * 20000 +
            existingTtPlayers * 25000 +
            existingTtCoaches * 20000 +
            existingObserverCosts;
          
          // 저장된 totalcosts가 있으면 이를 기준으로 사용 (환불이 적용된 금액)
          const savedTotal = data.basicInfo.totalcosts || 0;
          setSavedTotalCosts(savedTotal);
          
          // 인원 변경 감지를 위한 키 생성
          // 실제 코치 수(중복 제거 후)를 반영
          const participantKey = `${existingSparksPlayers}-${existingSparksCoaches}-${existingTtPlayers}-${existingTtCoaches}-${existingObservers.map(o => o.observerCount || 0).join('-')}`;
          setSavedParticipantCount(participantKey);
          
          // 임시 상태 초기화 (저장된 값으로 설정)
          setTempTotalCosts(savedTotal);
          setTempParticipantCount(participantKey);
          
          // originalCosts와 initialCosts 설정
          // 저장된 totalcosts가 있고 현재 인원 기준 금액과 다르면, 이미 환불이 적용된 상태일 수 있음
          // 하지만 환불 계산을 위해서는 환불 전 금액(현재 인원 기준 금액)을 기준으로 해야 함
          // 저장된 totalcosts는 환불이 적용된 금액일 수 있으므로, originalCosts는 현재 인원 기준 금액 사용
          // 이렇게 하면 인원이 변경되었을 때 환불 계산이 올바르게 됨
          // originalCosts와 initialCosts는 현재 인원 기준 금액으로 설정
          // 저장된 금액을 기준으로 환불 계산하므로, 현재 인원 기준 금액은 참고용으로만 사용
          setOriginalCosts(existingBaseCost);
          setInitialCosts(existingBaseCost);
          setIsExistingRegistration(true);
          const finalInitialCosts = savedTotal > 0 && savedTotal > existingBaseCost ? savedTotal : existingBaseCost;
          console.log('기존 등록 데이터 로드:', { 
            existingBaseCost, 
            savedTotal,
            originalCosts: existingBaseCost,
            initialCosts: finalInitialCosts,
            participantKey,
            existingSparksPlayers,
            existingSparksCoaches,
            existingTtPlayers,
            existingTtCoaches,
            existingObservers: existingObservers.map(o => o.observerCount || 0),
            isRefundApplied: savedTotal > 0 && savedTotal < existingBaseCost,
            note: '저장된 totalcosts는 환불 적용된 금액일 수 있음. 인원이 변경되지 않으면 저장된 금액 사용'
          });
        } else if (fetchedChurchName) {
          // basicInfo가 없어도 교회명은 설정
          setBasicInfo(prev => ({
            ...prev,
            church_name: fetchedChurchName,
          }));
          
          // 신규 등록: 임시 상태 초기화
          setTempTotalCosts(0);
          setTempParticipantCount('');
        } else {
          // 신규 등록: 임시 상태 초기화
          setTempTotalCosts(0);
          setTempParticipantCount('');
        }
        
        if (data.sparksList) {
          const updatedSparksList = data.sparksList.map((item: any, index: number) => {
            // 사진 경로가 있으면 photoPaths에 저장
            if (item.photoPath && item.id) {
              const photoKey = `sparks_${item.id}`;
              setPhotoPaths(prev => ({ ...prev, [photoKey]: item.photoPath }));
            }
            return {
              ...item,
              churchName: fetchedChurchName || item.churchName || basicInfo.church_name,
              coachContact: item.coachContact ? formatPhoneNumber(item.coachContact) : item.coachContact,
              uiNumber: index + 1,
              approved: item.approved || 0,
              rejectionReason: item.rejectionReason || null,
            };
          });
          setSparksList(updatedSparksList);
        }
        
        if (data.ttList) {
          const updatedTtList = data.ttList.map((item: any, index: number) => {
            // 사진 경로가 있으면 photoPaths에 저장
            if (item.photoPath && item.id) {
              const photoKey = `tt_${item.id}`;
              setPhotoPaths(prev => ({ ...prev, [photoKey]: item.photoPath }));
            }
            return {
              ...item,
              churchName: fetchedChurchName || item.churchName || basicInfo.church_name,
              coachContact: item.coachContact ? formatPhoneNumber(item.coachContact) : item.coachContact,
              uiNumber: index + 1,
              approved: item.approved || 0,
              rejectionReason: item.rejectionReason || null,
            };
          });
          setTtList(updatedTtList);
        }
        
        if (data.observers && data.observers.length > 0) {
          setObservers(prev => prev.map(observer => {
            const matched = data.observers.find(
              (item: any) => item.observerName === observer.observerName || item.observer_name === observer.observerName
            ) as any;
            if (matched) {
              return {
                ...observer,
                observerCount: matched.observerCount || matched.observer_count || 0,
                hasMeal: matched.hasMeal !== undefined ? matched.hasMeal : (matched.has_meal === 1),
                hasPin: matched.hasPin !== undefined ? matched.hasPin : (matched.has_pin === 1)
              };
            }
            return observer;
          }));
        }
      } catch (err: any) {
        console.error("Error fetching existing data:", err);
        // 기존 데이터가 없어도 에러로 처리하지 않음
      } finally {
        setLoading(false);
      }
    };

    fetchExistingData();
  }, [eventId, churchId, selectedChurchId]);

  const handleAddSparks = () => {
    const newId = sparksList.length > 0
      ? (sparksList[sparksList.length - 1].id || 0) + 1
      : 1;
    const newUiNumber = sparksList.length > 0
      ? (sparksList[sparksList.length - 1] as any).uiNumber + 1
      : 1;

    setSparksList([
      ...sparksList,
      {
        id: newId,
        handbook: "",
        churchName: basicInfo.church_name || "",
        playerName: "",
        coachName: "",
        coachContact: "",
        category: "Sparks",
        uiNumber: newUiNumber,
      } as any,
    ]);
  };

  const handleAddTt = () => {
    const newId = ttList.length > 0
      ? (ttList[ttList.length - 1].id || 0) + 1
      : 1;
    const newUiNumber = ttList.length > 0
      ? (ttList[ttList.length - 1] as any).uiNumber + 1
      : 1;

    setTtList([
      ...ttList,
      {
        id: newId,
        handbook: "",
        churchName: basicInfo.church_name || "",
        playerName: "",
        coachName: "",
        coachContact: "",
        category: "T&T",
        uiNumber: newUiNumber,
      } as any,
    ]);
  };

  const handleRemoveSparks = async (id: number) => {
    if (!eventId) return;
    
    try {
      await bqApi.deleteParticipant(eventId, id);
      
      // 환불 금액은 calculateCosts()에서 자동으로 계산됨
      // refundAmount는 더 이상 수동으로 누적하지 않음
      
      setSparksList(prev =>
        prev
          .filter(item => item.id !== id)
          .map((item, index) => ({ ...item, uiNumber: index + 1 } as any))
      );
    } catch (err: any) {
      console.error("Error deleting Sparks participant:", err);
      alert("삭제 중 문제가 발생했습니다.");
    }
  };

  const handleRemoveTt = async (id: number) => {
    if (!eventId) return;
    
    try {
      await bqApi.deleteParticipant(eventId, id);
      
      // 환불 금액은 calculateCosts()에서 자동으로 계산됨
      // refundAmount는 더 이상 수동으로 누적하지 않음
      
      setTtList(prev =>
        prev
          .filter(item => item.id !== id)
          .map((item, index) => ({ ...item, uiNumber: index + 1 } as any))
      );
    } catch (err: any) {
      console.error("Error deleting T&T participant:", err);
      alert("삭제 중 문제가 발생했습니다.");
    }
  };

  const handleSubmit = async () => {
    if (!eventId) {
      alert("이벤트 정보가 없습니다.");
      return;
    }

    // 기본 정보 검증
    if (!basicInfo.church_name || !basicInfo.church_number) {
      alert("교회 정보를 입력해주세요.");
      return;
    }

    try {
      setLoading(true);
      // churchId가 없으면 selectedChurchId 또는 basicInfo의 church_number를 사용
      const finalChurchId = churchId || selectedChurchId || basicInfo.church_number || '';
      // 교회명이 없으면 교회 ID로 조회
      let finalChurchName = basicInfo.church_name;
      if (!finalChurchName && finalChurchId) {
        try {
          const [mainId, subId] = finalChurchId.split('-');
          if (mainId && subId) {
            const churchResponse = await churchApi.getChurchById(mainId, subId);
            if (churchResponse.success && churchResponse.data) {
              finalChurchName = churchResponse.data.name;
            }
          }
        } catch (err) {
          console.error("Error fetching church name:", err);
        }
      }

      const finalTotalCosts = calculateCosts();
      await bqApi.register(eventId, finalChurchId, {
        basicInfo: {
          ...basicInfo,
          church_name: finalChurchName || basicInfo.church_name,
          totalcosts: finalTotalCosts,
        },
        sparksParticipants: sparksList.map(p => ({
          ...p,
          churchName: finalChurchName || p.churchName || basicInfo.church_name
        })),
        ttParticipants: ttList.map(p => ({
          ...p,
          churchName: finalChurchName || p.churchName || basicInfo.church_name
        })),
        observers: observers, // 모든 참관자 정보 저장 (observerCount가 0이어도 저장)
      });
      
      // 저장된 totalcosts 업데이트 (임시 상태를 저장된 상태로 이동)
      setSavedTotalCosts(finalTotalCosts);
      // 실제 코치 수(중복 제거 후)를 반영하기 위해 counts 사용
      const currentParticipantKey = `${counts.sparksPlayers}-${counts.sparksCoaches}-${counts.ttPlayers}-${counts.ttCoaches}-${observers.map(o => o.observerCount || 0).join('-')}`;
      setSavedParticipantCount(currentParticipantKey);
      
      // 임시 상태도 업데이트 (접수 완료 후에는 임시 상태 = 저장된 상태)
      setTempTotalCosts(finalTotalCosts);
      setTempParticipantCount(currentParticipantKey);
      
      // 등록 후 실제 DB ID를 받아오기 위해 데이터 다시 로드
      try {
        const registrationData = await bqApi.getRegistration(eventId, finalChurchId);
        if (registrationData.sparksList) {
          setSparksList(registrationData.sparksList.map((item: any, index: number) => {
            const photoKey = `sparks_${item.id}`;
            if (item.photoPath) {
              setPhotoPaths(prev => ({ ...prev, [photoKey]: item.photoPath }));
            }
            return {
              ...item,
              churchName: finalChurchName || item.churchName || basicInfo.church_name,
              coachContact: item.coachContact ? formatPhoneNumber(item.coachContact) : item.coachContact,
              uiNumber: index + 1,
              approved: item.approved || 0,
              rejectionReason: item.rejectionReason || null,
            };
          }));
        }
        if (registrationData.ttList) {
          setTtList(registrationData.ttList.map((item: any, index: number) => {
            const photoKey = `tt_${item.id}`;
            if (item.photoPath) {
              setPhotoPaths(prev => ({ ...prev, [photoKey]: item.photoPath }));
            }
            return {
              ...item,
              churchName: finalChurchName || item.churchName || basicInfo.church_name,
              coachContact: item.coachContact ? formatPhoneNumber(item.coachContact) : item.coachContact,
              uiNumber: index + 1,
              approved: item.approved || 0,
              rejectionReason: item.rejectionReason || null,
            };
          }));
        }
      } catch (err) {
        console.error("Error reloading registration data:", err);
      }
      
      alert("접수가 완료되었습니다!");
      navigate("/");
    } catch (err: any) {
      console.error("Error submitting data:", err);
      alert("접수 중 오류가 발생했습니다. 다시 시도해주세요. 계속 오류 발생시 본부로 문의해주세요.");
    } finally {
      setLoading(false);
    }
  };

  const calculateItems = () => {
    const sparksPins = sparksList.length;
    const ttPins = ttList.length;

    const uniqueSparksCoaches = new Set(
      sparksList.map((item) => `${item.coachName}-${item.coachContact}`)
    );
    const uniqueTtCoaches = new Set(
      ttList.map((item) => `${item.coachName}-${item.coachContact}`)
    );

    const sparksCoaches = uniqueSparksCoaches.size;
    const ttCoaches = uniqueTtCoaches.size;

    const sparkObserverPins = parseInt(observers[0]?.observerCount?.toString() || "0", 10);
    const ttObserverPins = parseInt(observers[2]?.observerCount?.toString() || "0", 10);
    const extraMealTickets = parseInt(observers[4]?.observerCount?.toString() || "0", 10);

    const totalMealTickets =
      sparksPins + ttPins + sparksCoaches + ttCoaches + extraMealTickets;

    const sparksStudent = sparksPins - sparksCoaches;
    const ttStudent = ttPins - ttCoaches;

    return {
      sparksPins,
      ttPins,
      sparkObserverPins,
      ttObserverPins,
      totalMealTickets,
      sparksCoaches,
      ttCoaches,
      sparksStudent,
      ttStudent,
    };
  };

  // 코치 핸드북 핀 계산 함수
  const calculateCoachPinsByHandbook = (list: BQParticipant[], handbookKey: string): number => {
    // 해당 핸드북의 선수들
    const handbookParticipants = list.filter((item) =>
      item.handbook && item.handbook.includes(handbookKey)
    );

    // 해당 핸드북 내 고유 코치 수 (동일 핸드북 내 동일 코치는 1개만)
    const uniqueCoachesInHandbook = new Set(
      handbookParticipants
        .filter((item) => item.coachName?.trim() && item.coachContact?.trim())
        .map((item) => `${item.coachName}-${item.coachContact}`)
    );

    return uniqueCoachesInHandbook.size;
  };

  const counts = {
    sparksPlayers: sparksList.filter(
      (item) => item.playerName?.trim()
    ).length,
    sparksCoaches: new Set(
      sparksList
        .filter((item) => item.coachName?.trim() && item.coachContact?.trim())
        .map((item) => `${item.coachName}-${item.coachContact}`)
    ).size,
    ttPlayers: ttList.filter(
      (item) => item.playerName?.trim()
    ).length,
    ttCoaches: new Set(
      ttList
        .filter((item) => item.coachName?.trim() && item.coachContact?.trim())
        .map((item) => `${item.coachName}-${item.coachContact}`)
    ).size,
    observers: observers.reduce(
      (total, obs) => total + parseInt(obs.observerCount?.toString() || "0", 10),
      0
    ),
    sparksCoachMarkers: parseInt(
      new Set(
        sparksList
          .filter((item) => item.coachName?.trim() && item.coachContact?.trim())
          .map((item) => `${item.coachName}-${item.coachContact}`)
      ).size.toString()
    ),
    ttCoachMarkers: parseInt(
      new Set(
        ttList
          .filter((item) => item.coachName?.trim() && item.coachContact?.trim())
          .map((item) => `${item.coachName}-${item.coachContact}`)
      ).size.toString()
    ),
    sparksPins: {
      행글라이더: sparksList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("행글라이더")
      ).length + calculateCoachPinsByHandbook(sparksList, "행글라이더"),
      윙러너: sparksList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("윙러너")
      ).length + calculateCoachPinsByHandbook(sparksList, "윙러너"),
      스카이스토머: sparksList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("스카이스토머")
      ).length + calculateCoachPinsByHandbook(sparksList, "스카이스토머"),
    },
    ttPins: {
      어드밴처1: ttList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("어드밴처1")
      ).length + calculateCoachPinsByHandbook(ttList, "어드밴처1"),
      어드밴처2: ttList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("어드밴처2")
      ).length + calculateCoachPinsByHandbook(ttList, "어드밴처2"),
      챌린지1: ttList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("챌린지1")
      ).length + calculateCoachPinsByHandbook(ttList, "챌린지1"),
      챌린지2: ttList.filter(
        (item) =>
          item.handbook &&
          item.handbook.includes("챌린지2")
      ).length + calculateCoachPinsByHandbook(ttList, "챌린지2"),
    },
  };

  // 환불 마감 시간 이후인지 확인
  const isAfterRefundDeadline = (): boolean => {
    if (!eventInfo?.bq_Refund_Deadline) return false;
    const deadline = moment(eventInfo.bq_Refund_Deadline);
    const now = moment();
    return now.isAfter(deadline);
  };

  // 환불 금액 계산 (줄인 인원의 금액에 환불 비율 적용)
  const calculateRefundAmount = (reducedAmount: number): number => {
    if (!isAfterRefundDeadline() || !eventInfo?.bq_Refund_Percentage) {
      return 0; // 환불 마감 시간 이전이거나 환불 비율이 없으면 환불 없음
    }
    const refundPercentage = eventInfo.bq_Refund_Percentage / 100;
    return reducedAmount * refundPercentage;
  };

  const calculateCosts = (): number => {
    // 현재 인원 기준 금액 계산
    const sparksCost = counts.sparksPlayers * 25000;
    const sparksCoachCost = counts.sparksCoaches * 20000;
    const ttCost = counts.ttPlayers * 25000;
    const ttCoachCost = counts.ttCoaches * 20000;
    const observerCosts =
      observers[0].observerCount * 12000 +
      observers[1].observerCount * 12000 +
      observers[2].observerCount * 12000 +
      observers[3].observerCount * 12000 +
      observers[4].observerCount * 8000;

    const currentBaseCost = sparksCost + sparksCoachCost + ttCost + ttCoachCost + observerCosts;
    
    // 현재 인원 변경 감지를 위한 키 생성
    const currentParticipantKey = `${counts.sparksPlayers}-${counts.sparksCoaches}-${counts.ttPlayers}-${counts.ttCoaches}-${observers.map(o => o.observerCount || 0).join('-')}`;
    
    // 현재 인원이 저장된 인원과 동일하면 저장된 금액 사용 (원점 복귀)
    if (savedParticipantCount !== '' && savedParticipantCount === currentParticipantKey && savedTotalCosts > 0) {
      // 원래 저장된 인원으로 돌아왔으므로 저장된 금액 사용
      // 상태 업데이트는 useEffect에서 처리
      return savedTotalCosts;
    }
    
    // 저장된 인원과 현재 인원이 다른지 확인
    const isParticipantChangedFromSaved = savedParticipantCount !== '' && savedParticipantCount !== currentParticipantKey;
    
    // 1. 진입 시: 저장된 인원과 현재 인원이 동일하면 저장된 금액 사용
    if (!isParticipantChangedFromSaved) {
      if (savedTotalCosts > 0) {
        // 저장된 인원과 동일하면 저장된 금액 사용
        return savedTotalCosts;
      }
      // 저장된 금액이 없으면 현재 인원 기준 금액 사용
      return currentBaseCost;
    }
    
    // 2. 접수 완료 이전 인원 변경 시
    // 환불 정책 기간일 때는 저장된 인원 수를 기준으로 계산
    if (isParticipantChangedFromSaved && savedTotalCosts > 0 && savedParticipantCount !== '') {
      // 저장된 인원 키 파싱: sparksPlayers-sparksCoaches-ttPlayers-ttCoaches-observer0-observer1-observer2-observer3-observer4
      const savedParts = savedParticipantCount.split('-').map(Number);
      const currentParts = currentParticipantKey.split('-').map(Number);
      
      if (savedParts.length === currentParts.length && savedParts.length >= 9) {
        // 각 인원 타입별로 증가/감소 금액 계산 (저장된 인원 기준)
        let addedAmount = 0; // 증가한 금액
        let reducedAmount = 0; // 감소한 금액
        
        // Sparks 선수
        const sparksPlayersDiff = currentParts[0] - savedParts[0];
        if (sparksPlayersDiff > 0) addedAmount += sparksPlayersDiff * 25000;
        else if (sparksPlayersDiff < 0) reducedAmount += Math.abs(sparksPlayersDiff) * 25000;
        
        // Sparks 코치
        const sparksCoachesDiff = currentParts[1] - savedParts[1];
        if (sparksCoachesDiff > 0) addedAmount += sparksCoachesDiff * 20000;
        else if (sparksCoachesDiff < 0) reducedAmount += Math.abs(sparksCoachesDiff) * 20000;
        
        // TT 선수
        const ttPlayersDiff = currentParts[2] - savedParts[2];
        if (ttPlayersDiff > 0) addedAmount += ttPlayersDiff * 25000;
        else if (ttPlayersDiff < 0) reducedAmount += Math.abs(ttPlayersDiff) * 25000;
        
        // TT 코치
        const ttCoachesDiff = currentParts[3] - savedParts[3];
        if (ttCoachesDiff > 0) addedAmount += ttCoachesDiff * 20000;
        else if (ttCoachesDiff < 0) reducedAmount += Math.abs(ttCoachesDiff) * 20000;
        
        // 참관자 (5명)
        for (let i = 0; i < 5; i++) {
          const observerDiff = currentParts[4 + i] - savedParts[4 + i];
          const observerPrice = i === 4 ? 8000 : 12000; // 마지막 참관자는 8000원
          if (observerDiff > 0) addedAmount += observerDiff * observerPrice;
          else if (observerDiff < 0) reducedAmount += Math.abs(observerDiff) * observerPrice;
        }
        
        // 계산된 금액 (저장된 금액 기준)
        // 증가와 감소를 동시에 처리
        let newTotalCosts = savedTotalCosts;
        
        // 인원 추가: 저장된 금액에 추가 금액 더하기
        if (addedAmount > 0) {
          newTotalCosts += addedAmount;
        }
        
        // 인원 감소: 환불 정책 여부에 따라 차감
        if (reducedAmount > 0) {
          // 환불 정책 기간 여부 재확인 (함수 호출로 최신 값 확인)
          const isAfterDeadlineNow = isAfterRefundDeadline();
          const refundPercentage = eventInfo?.bq_Refund_Percentage;
          const hasRefundPolicyNow = refundPercentage !== undefined && refundPercentage !== null;
          
          console.log('환불 계산:', {
            savedTotalCosts,
            reducedAmount,
            isAfterDeadlineNow,
            refundPercentage,
            hasRefundPolicyNow,
            currentParticipantKey,
            savedParticipantCount
          });
          
          if (isAfterDeadlineNow && hasRefundPolicyNow && refundPercentage !== undefined) {
            // 정책 기간: 해당 금액에 정책%만큼 한 값을 -
            const refund = reducedAmount * (refundPercentage / 100);
            console.log('환불 정책 적용:', { refund, newTotalCosts: newTotalCosts - refund });
            newTotalCosts -= refund;
          } else {
            // 정책 기간 아님: 해당 금액만큼 -
            console.log('환불 정책 미적용:', { newTotalCosts: newTotalCosts - reducedAmount });
            newTotalCosts -= reducedAmount;
          }
        }
        
        // 상태 업데이트는 useEffect에서 처리 (렌더링 중 상태 업데이트 방지)
        return newTotalCosts;
      }
    }
    
    // 저장된 인원 정보가 없거나 환불 정책 기간이 아닐 때는 현재 인원 기준 금액 사용
    // (저장된 인원과 다르지만 저장된 인원 정보가 없는 경우)
    if (savedParticipantCount === '') {
      return currentBaseCost;
    }
    
    // 저장된 금액이 없거나 인원 키 파싱 실패 시: 현재 인원 기준 금액 사용
    return currentBaseCost;
  };
  
  // 인원 변경 시 임시 상태 업데이트 (useEffect로 처리하여 무한 렌더링 방지)
  useEffect(() => {
    const currentParticipantKey = `${counts.sparksPlayers}-${counts.sparksCoaches}-${counts.ttPlayers}-${counts.ttCoaches}-${observers.map(o => o.observerCount || 0).join('-')}`;
    
    // 현재 인원이 저장된 인원과 동일하면 저장된 값으로 초기화
    if (savedParticipantCount !== '' && savedParticipantCount === currentParticipantKey && savedTotalCosts > 0) {
      setTempTotalCosts(savedTotalCosts);
      setTempParticipantCount(savedParticipantCount);
      return;
    }
    
    // 인원이 변경되었거나 신규 등록인 경우
    // 환불 정책 기간일 때는 저장된 인원 수를 기준으로 계산
    const isAfterDeadline = isAfterRefundDeadline();
    const hasRefundPolicy = eventInfo?.bq_Refund_Percentage !== undefined && eventInfo.bq_Refund_Percentage !== null;
    
    if (savedParticipantCount !== '' && savedParticipantCount !== currentParticipantKey && savedTotalCosts > 0) {
      // 저장된 인원 수를 기준으로 계산
      const savedParts = savedParticipantCount.split('-').map(Number);
      const currentParts = currentParticipantKey.split('-').map(Number);
      
      if (savedParts.length === currentParts.length && savedParts.length >= 9) {
        let addedAmount = 0;
        let reducedAmount = 0;
        
        // 각 인원 타입별로 증가/감소 금액 계산 (저장된 인원 기준)
        const sparksPlayersDiff = currentParts[0] - savedParts[0];
        if (sparksPlayersDiff > 0) addedAmount += sparksPlayersDiff * 25000;
        else if (sparksPlayersDiff < 0) reducedAmount += Math.abs(sparksPlayersDiff) * 25000;
        
        const sparksCoachesDiff = currentParts[1] - savedParts[1];
        if (sparksCoachesDiff > 0) addedAmount += sparksCoachesDiff * 20000;
        else if (sparksCoachesDiff < 0) reducedAmount += Math.abs(sparksCoachesDiff) * 20000;
        
        const ttPlayersDiff = currentParts[2] - savedParts[2];
        if (ttPlayersDiff > 0) addedAmount += ttPlayersDiff * 25000;
        else if (ttPlayersDiff < 0) reducedAmount += Math.abs(ttPlayersDiff) * 25000;
        
        const ttCoachesDiff = currentParts[3] - savedParts[3];
        if (ttCoachesDiff > 0) addedAmount += ttCoachesDiff * 20000;
        else if (ttCoachesDiff < 0) reducedAmount += Math.abs(ttCoachesDiff) * 20000;
        
        for (let i = 0; i < 5; i++) {
          const observerDiff = currentParts[4 + i] - savedParts[4 + i];
          const observerPrice = i === 4 ? 8000 : 12000;
          if (observerDiff > 0) addedAmount += observerDiff * observerPrice;
          else if (observerDiff < 0) reducedAmount += Math.abs(observerDiff) * observerPrice;
        }
        
        // 증가와 감소를 동시에 처리
        let newTotalCosts = savedTotalCosts;
        if (addedAmount > 0) {
          newTotalCosts += addedAmount;
        }
        if (reducedAmount > 0) {
          // 환불 정책 기간 여부 재확인 (함수 호출로 최신 값 확인)
          const isAfterDeadlineNow = isAfterRefundDeadline();
          const hasRefundPolicyNow = eventInfo?.bq_Refund_Percentage !== undefined && eventInfo.bq_Refund_Percentage !== null;
          
          if (isAfterDeadlineNow && hasRefundPolicyNow && eventInfo.bq_Refund_Percentage !== undefined) {
            const refund = reducedAmount * (eventInfo.bq_Refund_Percentage / 100);
            newTotalCosts -= refund;
          } else {
            newTotalCosts -= reducedAmount;
          }
        }
        
        setTempTotalCosts(newTotalCosts);
        setTempParticipantCount(currentParticipantKey);
      }
    } else if (savedParticipantCount === '') {
      // 신규 등록인 경우: 현재 인원 기준 금액 계산
      const currentBaseCost = 
        counts.sparksPlayers * 25000 +
        counts.sparksCoaches * 20000 +
        counts.ttPlayers * 25000 +
        counts.ttCoaches * 20000 +
        observers[0].observerCount * 12000 +
        observers[1].observerCount * 12000 +
        observers[2].observerCount * 12000 +
        observers[3].observerCount * 12000 +
        observers[4].observerCount * 8000;
      
      if (currentBaseCost > 0) {
        setTempTotalCosts(currentBaseCost);
        setTempParticipantCount(currentParticipantKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts.sparksPlayers, counts.sparksCoaches, counts.ttPlayers, counts.ttCoaches, observers.map(o => o.observerCount).join('-'), savedParticipantCount, savedTotalCosts, eventInfo?.bq_Refund_Percentage, eventInfo?.bq_Refund_Deadline]);

  const items = calculateItems();
  const totalcosts = calculateCosts();
  
  // initialCosts는 더 이상 사용하지 않음 (저장된 금액만 기준으로 사용)
  // 주석 처리하거나 제거 가능하지만, 호환성을 위해 유지
  // useEffect(() => {
  //   // initialCosts 업데이트 로직 제거 (저장된 금액만 기준으로 사용)
  // }, [sparksList, ttList, observers]);

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    const stringValue = value as string;
    
    // 연락처 필드인 경우 전화번호 포맷팅 적용
    if (name === "contact") {
      const formatted = formatPhoneNumber(stringValue);
      if (formatted.length <= 13) {
        setBasicInfo((prev) => ({ ...prev, [name]: formatted }));
      }
    } else {
      setBasicInfo((prev) => ({ ...prev, [name as string]: stringValue.replace(/\s+/g, "") }));
    }
  };

  const handleSparksChange = (index: number, field: string, value: string) => {
    setSparksList((prevSparksList) =>
      prevSparksList.map((spark, i) =>
        i === index
          ? {
              ...spark,
              [field]: field === "handbook" 
                ? value 
                : field === "coachContact"
                ? (() => {
                    const formatted = formatPhoneNumber(value);
                    return formatted.length <= 13 ? formatted : spark.coachContact;
                  })()
                : value.replace(/\s+/g, ""),
            } as any
          : spark
      )
    );
  };

  const handleTtChange = (index: number, field: string, value: string) => {
    setTtList((prevTtList) =>
      prevTtList.map((tt, i) =>
        i === index
          ? { 
              ...tt, 
              [field]: field === "handbook" 
                ? value 
                : field === "coachContact"
                ? (() => {
                    const formatted = formatPhoneNumber(value);
                    return formatted.length <= 13 ? formatted : tt.coachContact;
                  })()
                : value.replace(/\s+/g, "") 
            } as any
          : tt
      )
    );
  };

  const handleObserverChange = (index: number, field: string, value: string | number) => {
    setObservers((prevObservers) => {
      const newObservers = prevObservers.map((observer, i) =>
        i === index
          ? { 
              ...observer, 
              [field]: field === "count" 
                ? (() => {
                    if (typeof value === "number") {
                      return Math.max(0, value);
                    }
                    const strValue = String(value);
                    if (strValue === "" || strValue === "-") {
                      return 0;
                    }
                    const numValue = parseInt(strValue, 10);
                    return isNaN(numValue) ? 0 : Math.max(0, numValue);
                  })()
                : String(value).replace(/\s+/g, "") 
            } as any
          : observer
      );
      
      // 참관자 인원 변경 시 환불 금액은 calculateCosts()에서 자동으로 계산됨
      // refundAmount는 더 이상 수동으로 누적하지 않음
      
      return newObservers;
    });
  };

  // 교회 검색 함수
  const handleChurchSearch = async (value: string) => {
    if (!value) {
      setChurches([]);
      return;
    }

    try {
      setChurchSearchLoading(true);
      const isMainId = /^\d{3,4}$/.test(value);
      let searchParams = {};
      
      if (isMainId) {
        // 등록번호로 검색 (3-4자리)
        const response = await churchApi.searchChurches({
          getAllResults: true
        });
        if (response.success) {
          // mainId에 포함되는 교회 필터링
          const filtered = response.data.filter(church => 
            church.mainId.includes(value)
          );
          setChurches(filtered);
        }
      } else {
        // 교회명으로 검색
        searchParams = { name: value };
        const response = await churchApi.searchChurches({
          ...searchParams,
          getAllResults: true
        });
        if (response.success) {
          setChurches(response.data);
        }
      }
    } catch (err) {
      console.error('Error searching churches:', err);
      setError('교회 검색에 실패했습니다.');
    } finally {
      setChurchSearchLoading(false);
    }
  };

  // 교회 선택 시 basicInfo 자동 채우기 및 기존 데이터 조회
  const handleChurchSelect = (church: Church | null) => {
    if (church) {
      const churchIdString = `${church.mainId}-${church.subId}`;
      setSelectedChurchId(churchIdString);
      
      setChurchInfo({
        mainId: church.mainId,
        subId: church.subId,
        name: church.name,
        managerName: church.managerName,
        phone: church.phone,
      });
      
      // 전화번호 포맷팅 적용
      const formattedPhone = church.phone ? formatPhoneNumber(church.phone) : "";
      
      setBasicInfo(prev => ({
        ...prev,
        church_number: church.mainId,
        church_name: church.name,
        representative: church.managerName || "",
        contact: formattedPhone,
      }));

      // 선수명단의 교회명도 업데이트
      setSparksList(prev => prev.map(item => ({
        ...item,
        churchName: church.name
      })));
      setTtList(prev => prev.map(item => ({
        ...item,
        churchName: church.name
      })));
    } else {
      setSelectedChurchId(null);
    }
  };

  // 사진 업로드 핸들러
  const handlePhotoUpload = async (
    participantId: number | undefined,
    type: "sparks" | "tt",
    playerName: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!eventId || !event.target.files?.[0]) {
      alert("이벤트 정보가 없거나 파일을 선택해주세요.");
      return;
    }

    // participantId가 없으면 (새로 추가한 선수인 경우) 먼저 등록을 저장해야 함
    if (!participantId) {
      alert("사진을 업로드하려면 먼저 '접수 완료' 버튼을 눌러 등록을 저장해주세요. 등록 후 다시 접수 페이지로 돌아와 사진을 업로드할 수 있습니다.");
      event.target.value = '';
      return;
    }

    // 승인된 선수는 사진 업로드 불가
    const participant = type === "sparks" 
      ? sparksList.find(p => p.id === participantId)
      : ttList.find(p => p.id === participantId);
    
    if (participant && participant.approved === 1) {
      alert("승인된 선수는 사진을 변경할 수 없습니다.");
      event.target.value = '';
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("파일 크기는 5MB 이하여야 합니다.");
      return;
    }

    try {
      setLoading(true);
      
      const result = await bqApi.uploadPhoto(
        eventId,
        type,
        participantId,
        file,
        playerName,
        basicInfo.church_name,
        basicInfo.region
      );

      if (result.success && result.photo_path) {
        // 사진 경로 저장
        const photoKey = `${type}_${participantId}`;
        setPhotoPaths(prev => ({ ...prev, [photoKey]: result.photo_path! }));
        alert("사진 업로드 성공! 하단에 접수 완료 버튼을 눌러 접수를 완료해주세요.");
      } else {
        alert("사진 업로드에 실패했습니다.");
      }
    } catch (err: any) {
      console.error("Error uploading photo:", err);
      alert("사진 업로드 실패: " + (err.message || "알 수 없는 오류"));
    } finally {
      setLoading(false);
      // 파일 input 초기화
      event.target.value = '';
    }
  };

  if (loading && !churchInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 1, fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          성경퀴즈대회 참가 신청
        </Typography>
        <Typography variant="body1" color="text.secondary">
          참가 신청서를 작성해주세요
        </Typography>
      </Box>
      
      {/* 환불 정책 공지 */}
      {eventInfo && (eventInfo.bq_Refund_Deadline || (eventInfo.bq_Refund_Percentage !== undefined && eventInfo.bq_Refund_Percentage !== null)) && (
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<MoneyIcon />}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            환불 정책 안내
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {eventInfo.bq_Refund_Deadline && (
              <li>
                <Typography variant="body2">
                  환불 마감 시간: <strong>{moment(eventInfo.bq_Refund_Deadline).format('YYYY년 MM월 DD일 HH:mm')}</strong>
                </Typography>
              </li>
            )}
            {eventInfo.bq_Refund_Deadline && eventInfo.bq_Refund_Percentage !== undefined && eventInfo.bq_Refund_Percentage !== null && (
              <li>
                <Typography variant="body2">
                  전액 환불 가능 마감 시간 <strong>이전</strong>: 참가비 <strong>100% 환불</strong>
                </Typography>
              </li>
            )}
            {eventInfo.bq_Refund_Deadline && eventInfo.bq_Refund_Percentage !== undefined && eventInfo.bq_Refund_Percentage !== null && (
              <li>
                <Typography variant="body2">
                  환불 비율 적용 마감 시간 <strong>이후</strong>: 참가비 <strong>{eventInfo.bq_Refund_Percentage}% 환불</strong>
                  {isAfterRefundDeadline() && (
                    <Chip 
                      label="현재 환불 비율 적용 마감 시간 이후입니다" 
                      color="warning" 
                      size="small" 
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              </li>
            )}
            {!eventInfo.bq_Refund_Deadline && eventInfo.bq_Refund_Percentage !== undefined && eventInfo.bq_Refund_Percentage !== null && (
              <li>
                <Typography variant="body2">
                  환불 비율: <strong>{eventInfo.bq_Refund_Percentage}%</strong>
                </Typography>
              </li>
            )}
          </Box>
        </Alert>
      )}
      
      <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'primary.main', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChurchIcon sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            기본 정보
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <InputLabel id="region-label" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" fontSize="small" />
              참가지역 선택
            </InputLabel>
            <Select
              labelId="region-label"
              value={basicInfo.region}
              name="region"
              onChange={(e) => setBasicInfo(prev => ({ ...prev, region: e.target.value as string }))}
              fullWidth
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="충청">충청 (청주서남교회)</MenuItem>
              <MenuItem value="수도권 1차">수도권 1차 (금란교회)</MenuItem>
              <MenuItem value="경상">경상 (경산중앙교회)</MenuItem>
              <MenuItem value="전라">전라(전주바울교회)</MenuItem>
              <MenuItem value="수도권 2차">수도권 2차 (분당 할렐루야교회)</MenuItem>
            </Select>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={churches}
              getOptionLabel={(option) => `${option.mainId}-${option.subId} ${option.name}`}
              loading={churchSearchLoading}
              onInputChange={(event, newInputValue) => {
                setChurchSearchValue(newInputValue);
                if (newInputValue.length >= 2) {
                  handleChurchSearch(newInputValue);
                } else {
                  setChurches([]);
                }
              }}
              onChange={(event, newValue) => {
                handleChurchSelect(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="교회 검색 (등록번호 또는 교회명)"
                  placeholder="등록번호 3-4자리 또는 교회명 입력"
                  fullWidth
                  sx={{ borderRadius: 2 }}
                />
              )}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                value={basicInfo.church_number}
                name="church_number"
                onChange={handleBasicInfoChange}
                fullWidth
                disabled
                label="등록번호"
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="교회명"
                value={basicInfo.church_name}
                name="church_name"
                disabled
                onChange={handleBasicInfoChange}
                fullWidth
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="담당자"
                value={basicInfo.representative}
                name="representative"
                onChange={handleBasicInfoChange}
                fullWidth
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                value={basicInfo.contact}
                name="contact"
                onChange={handleBasicInfoChange}
                fullWidth
                label="연락처"
                placeholder="010-0000-0000"
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sparks 선수 */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'secondary.main', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SportsIcon sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            Sparks 선수 명단
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {sparksList.map((item, index) => (
            <Paper key={item.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={0.5}>
                  {item.approved === 1 ? (
                    <CheckIcon color="success" sx={{ fontSize: 28 }} />
                  ) : item.rejectionReason ? (
                    <Tooltip title={item.rejectionReason} arrow placement="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <PendingIcon color="warning" sx={{ fontSize: 28 }} />
                        <Chip 
                          label="반려" 
                          color="error" 
                          size="small" 
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Grid>
                <Grid item xs={12} sm={0.5}>
                  <Chip label={(item as any).uiNumber} color="primary" size="small" />
                </Grid>
                <Grid item xs={12} sm={2.5}>
                  <Select
                    value={item.handbook}
                    onChange={(e) => handleSparksChange(index, "handbook", e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="행글라이더 1학년">행글라이더 1학년</MenuItem>
                    <MenuItem value="행글라이더 2학년">행글라이더 2학년</MenuItem>
                    <MenuItem value="윙러너 1학년">윙러너 1학년</MenuItem>
                    <MenuItem value="윙러너 2학년">윙러너 2학년</MenuItem>
                    <MenuItem value="스카이스토머 2학년">스카이스토머 2학년</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    value={basicInfo.church_name}
                    onChange={(e) => handleSparksChange(index, "churchName", e.target.value)}
                    placeholder="교회명"
                    size="small"
                    fullWidth
                    disabled
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <TextField
                    value={item.playerName}
                    onChange={(e) => handleSparksChange(index, "playerName", e.target.value)}
                    placeholder="선수이름"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <TextField
                    value={item.coachName}
                    onChange={(e) => handleSparksChange(index, "coachName", e.target.value)}
                    placeholder="코치이름"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    value={item.coachContact}
                    onChange={(e) => handleSparksChange(index, "coachContact", e.target.value)}
                    placeholder="010-0000-0000"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id={`sparks-photo-${item.id}`}
                      type="file"
                      onChange={(e) => handlePhotoUpload(item.id, "sparks", item.playerName || "", e)}
                      disabled={item.approved === 1}
                    />
                    <label htmlFor={`sparks-photo-${item.id}`}>
                      <IconButton 
                        color="primary" 
                        component="span" 
                        size="small"
                        disabled={item.approved === 1}
                        title={item.approved === 1 ? "승인된 선수는 사진을 변경할 수 없습니다" : "사진 업로드"}
                      >
                        <PhotoCameraIcon />
                      </IconButton>
                    </label>
                    {photoPaths[`sparks_${item.id}`] && (
                      <img
                        src={bqApi.getPhotoUrl(eventId || "", photoPaths[`sparks_${item.id}`])}
                        alt="Uploaded"
                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: 4 }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={true}
                      onClick={() => {
                        if (window.confirm("정말 삭제하시겠습니까?")) {
                          handleRemoveSparks(item.id!);
                        }
                      }}
                    >
                      삭제
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button 
            variant="contained" 
            onClick={handleAddSparks} 
            startIcon={<GroupIcon />}
            disabled={true}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            선수 추가
          </Button>
        </CardContent>
      </Card>

      {/* T&T 선수 */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'info.main', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SportsIcon sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            T&T 선수 명단
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {ttList.map((item, index) => (
            <Paper key={item.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={0.5}>
                  {item.approved === 1 ? (
                    <CheckIcon color="success" sx={{ fontSize: 28 }} />
                  ) : item.rejectionReason ? (
                    <Tooltip title={item.rejectionReason} arrow placement="right">
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <PendingIcon color="warning" sx={{ fontSize: 28 }} />
                        <Chip 
                          label="반려" 
                          color="error" 
                          size="small" 
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Grid>
                <Grid item xs={12} sm={0.5}>
                  <Chip label={(item as any).uiNumber} color="info" size="small" />
                </Grid>
                <Grid item xs={12} sm={2.5}>
                  <Select
                    value={item.handbook}
                    onChange={(e) => handleTtChange(index, "handbook", e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="어드밴처1권 3학년">어드밴처1권 3학년</MenuItem>
                    <MenuItem value="어드밴처1권 4학년">어드밴처1권 4학년</MenuItem>
                    <MenuItem value="어드밴처2권 4학년">어드밴처2권 4학년</MenuItem>
                    <MenuItem value="챌린지1권 5학년">챌린지1권 5학년</MenuItem>
                    <MenuItem value="챌린지1권 6학년">챌린지1권 6학년</MenuItem>
                    <MenuItem value="챌린지2권 6학년">챌린지2권 6학년</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    value={basicInfo.church_name}
                    onChange={(e) => handleTtChange(index, "churchName", e.target.value)}
                    placeholder="교회명"
                    size="small"
                    fullWidth
                    disabled
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <TextField
                    value={item.playerName}
                    onChange={(e) => handleTtChange(index, "playerName", e.target.value)}
                    placeholder="선수이름"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <TextField
                    value={item.coachName}
                    onChange={(e) => handleTtChange(index, "coachName", e.target.value)}
                    placeholder="코치이름"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    value={item.coachContact}
                    onChange={(e) => handleTtChange(index, "coachContact", e.target.value)}
                    placeholder="010-0000-0000"
                    size="small"
                    fullWidth
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={1.5}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <input
                      accept="image/*"
                      style={{ display: "none" }}
                      id={`tt-photo-${item.id}`}
                      type="file"
                      onChange={(e) => handlePhotoUpload(item.id, "tt", item.playerName || "", e)}
                      disabled={item.approved === 1}
                    />
                    <label htmlFor={`tt-photo-${item.id}`}>
                      <IconButton 
                        color="primary" 
                        component="span" 
                        size="small"
                        disabled={item.approved === 1}
                        title={item.approved === 1 ? "승인된 선수는 사진을 변경할 수 없습니다" : "사진 업로드"}
                      >
                        <PhotoCameraIcon />
                      </IconButton>
                    </label>
                    {photoPaths[`tt_${item.id}`] && (
                      <img
                        src={bqApi.getPhotoUrl(eventId || "", photoPaths[`tt_${item.id}`])}
                        alt="Uploaded"
                        style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: 4 }}
                      />
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      disabled={true}
                      onClick={() => {
                        if (window.confirm("정말 삭제하시겠습니까?")) {
                          handleRemoveTt(item.id!);
                        }
                      }}
                    >
                      삭제
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button 
            variant="contained" 
            onClick={handleAddTt} 
            startIcon={<GroupIcon />}
            disabled={true}
            sx={{ mt: 2, borderRadius: 2 }}
          >
            선수 추가
          </Button>
        </CardContent>
      </Card>

      {/* 참관자 */}
      <Card elevation={3} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ bgcolor: 'success.main', p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon sx={{ color: 'white' }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            선수 외의 참관자
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {observers.map((observer, index) => (
            <Paper key={observer.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="body1" fontWeight={500}>
                    {observer.observerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    type="number"
                    value={observerInputs[observer.id] !== undefined 
                      ? observerInputs[observer.id] 
                      : (observer.observerCount || "")}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setObserverInputs(prev => ({ ...prev, [observer.id]: inputValue }));
                      if (inputValue !== "" && !isNaN(Number(inputValue)) && Number(inputValue) >= 0) {
                        setObservers(prev => prev.map((obs, i) => 
                          i === index ? { ...obs, observerCount: Math.floor(Number(inputValue)) } : obs
                        ));
                      }
                    }}
                    onBlur={(e) => {
                      const inputValue = e.target.value;
                      const finalValue = inputValue === "" || isNaN(Number(inputValue)) || Number(inputValue) < 0
                        ? 0
                        : Math.floor(Number(inputValue));
                      setObservers(prev => prev.map((obs, i) => 
                        i === index ? { ...obs, observerCount: finalValue } : obs
                      ));
                      setObserverInputs(prev => {
                        const newInputs = { ...prev };
                        delete newInputs[observer.id];
                        return newInputs;
                      });
                    }}
                    placeholder="0"
                    size="small"
                    fullWidth
                    inputProps={{ 
                      min: 0,
                      step: 1
                    }}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      icon={observer.hasMeal ? <CheckIcon /> : undefined}
                      label={observer.hasMeal ? "식권 포함" : "식권 없음"} 
                      color={observer.hasMeal ? "success" : "default"}
                      size="small"
                    />
                    <Chip 
                      icon={observer.hasPin ? <CheckIcon /> : undefined}
                      label={observer.hasPin ? "참가핀 포함" : "참가핀 없음"} 
                      color={observer.hasPin ? "primary" : "default"}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </CardContent>
      </Card>

      <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <MoneyIcon sx={{ color: 'white' }} />
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              접수 내역
            </Typography>
          </Box>
          <Divider sx={{ mb: 3, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                  참가비용 안내
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color="white">Sparks/T&T 선수: 25,000원</Typography>
                  <Typography color="white">Sparks/T&T 코치: 20,000원</Typography>
                  <Typography color="white">참관자: 12,000원</Typography>
                  <Typography color="white">추가 식사자: 8,000원</Typography>
                </Box>
              </Paper>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                  Sparks 핀
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                  <Typography color="white" variant="body2">행글라이더: {counts.sparksPins["행글라이더"]}개</Typography>
                  <Typography color="white" variant="body2">윙러너: {counts.sparksPins["윙러너"]}개</Typography>
                  <Typography color="white" variant="body2">스카이스토머: {counts.sparksPins["스카이스토머"]}개</Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                  T&T 핀
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography color="white" variant="body2">어드밴처1: {counts.ttPins["어드밴처1"]}개</Typography>
                  <Typography color="white" variant="body2">어드밴처2: {counts.ttPins["어드밴처2"]}개</Typography>
                  <Typography color="white" variant="body2">챌린지1: {counts.ttPins["챌린지1"]}개</Typography>
                  <Typography color="white" variant="body2">챌린지2: {counts.ttPins["챌린지2"]}개</Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                  최종 수령 품목 및 수량
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color="white" variant="body2">
                    Sparks 참관핀: {items.sparkObserverPins + parseInt(observers[1]?.observerCount?.toString() || "0", 10)}개
                  </Typography>
                  <Typography color="white" variant="body2">
                    T&T 참관핀: {items.ttObserverPins + parseInt(observers[3]?.observerCount?.toString() || "0", 10)}개
                  </Typography>
                  <Typography color="white" variant="body2">
                    Sparks 코치 표식: {counts.sparksCoachMarkers}개
                  </Typography>
                  <Typography color="white" variant="body2">
                    T&T 코치 표식: {counts.ttCoachMarkers}개
                  </Typography>
                  <Typography color="white" variant="body2">
                    총 식권: {counts.sparksPlayers + counts.sparksCoaches + counts.ttPlayers + counts.ttCoaches + counts.observers}장
                  </Typography>
                </Box>
              </Paper>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                  최종 참가 인원
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography color="white" variant="body2">Sparks 선수: {counts.sparksPlayers}명</Typography>
                  <Typography color="white" variant="body2">Sparks 코치: {counts.sparksCoaches}명</Typography>
                  <Typography color="white" variant="body2">T&T 선수: {counts.ttPlayers}명</Typography>
                  <Typography color="white" variant="body2">T&T 코치: {counts.ttCoaches}명</Typography>
                  <Typography color="white" variant="body2">참관자 + 추가 식사자: {counts.observers}명</Typography>
                </Box>
              </Paper>
              <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.2)', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                  총 참가비
                </Typography>
                {(() => {
                  const currentBaseCost = 
                    counts.sparksPlayers * 25000 +
                    counts.sparksCoaches * 20000 +
                    counts.ttPlayers * 25000 +
                    counts.ttCoaches * 20000 +
                    observers[0].observerCount * 12000 +
                    observers[1].observerCount * 12000 +
                    observers[2].observerCount * 12000 +
                    observers[3].observerCount * 12000 +
                    observers[4].observerCount * 8000;
                  
                  // 인원 변경 감지 (저장된 인원 기준으로 안정적으로 계산)
                  const currentParticipantKey = `${counts.sparksPlayers}-${counts.sparksCoaches}-${counts.ttPlayers}-${counts.ttCoaches}-${observers.map(o => o.observerCount || 0).join('-')}`;
                  
                  let displayRefundAmount = 0;
                  
                  // 환불 정책 기간일 때는 저장된 인원 수를 기준으로 계산 (깜빡임 방지)
                  if (savedParticipantCount !== '' && savedParticipantCount !== currentParticipantKey && savedTotalCosts > 0) {
                    // 저장된 인원 정보와 현재 인원 정보를 직접 비교
                    const savedParts = savedParticipantCount.split('-').map(Number);
                    const currentParts = currentParticipantKey.split('-').map(Number);
                    
                    if (savedParts.length === currentParts.length && savedParts.length >= 9) {
                      let reducedAmount = 0;
                      
                      // 각 인원 타입별로 감소 금액 계산 (저장된 인원 기준)
                      const sparksPlayersDiff = currentParts[0] - savedParts[0];
                      if (sparksPlayersDiff < 0) reducedAmount += Math.abs(sparksPlayersDiff) * 25000;
                      
                      const sparksCoachesDiff = currentParts[1] - savedParts[1];
                      if (sparksCoachesDiff < 0) reducedAmount += Math.abs(sparksCoachesDiff) * 20000;
                      
                      const ttPlayersDiff = currentParts[2] - savedParts[2];
                      if (ttPlayersDiff < 0) reducedAmount += Math.abs(ttPlayersDiff) * 25000;
                      
                      const ttCoachesDiff = currentParts[3] - savedParts[3];
                      if (ttCoachesDiff < 0) reducedAmount += Math.abs(ttCoachesDiff) * 20000;
                      
                      for (let i = 0; i < 5; i++) {
                        const observerDiff = currentParts[4 + i] - savedParts[4 + i];
                        const observerPrice = i === 4 ? 8000 : 12000;
                        if (observerDiff < 0) reducedAmount += Math.abs(observerDiff) * observerPrice;
                      }
                      
                      // 인원 감소 + 정책 기간: 환불 금액 계산
                      if (reducedAmount > 0 && isAfterRefundDeadline() && eventInfo?.bq_Refund_Percentage !== undefined) {
                        displayRefundAmount = reducedAmount * (eventInfo.bq_Refund_Percentage / 100);
                      }
                    }
                  }
                  
                  return displayRefundAmount > 0 ? (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 0.5 }}>
                        환불 금액: -{new Intl.NumberFormat().format(displayRefundAmount)}원
                      </Typography>
                      {eventInfo?.bq_Refund_Percentage && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                          (환불 비율 적용 마감 시간 이후 인원 감소로 인한 {eventInfo.bq_Refund_Percentage}% 환불 적용)
                        </Typography>
                      )}
                    </Box>
                  ) : null;
                })()}
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                  {new Intl.NumberFormat().format(totalcosts)}원
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
          sx={{ 
            px: 4, 
            py: 1.5, 
            borderRadius: 3,
            fontSize: '1.1rem',
            fontWeight: 600,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
            }
          }}
        >
          {loading ? "처리 중..." : "접수 완료"}
        </Button>
      </Box>
    </Container>
  );
};


export default BQRegisterPage;

